import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { createNewEncounter } from '../../redux/slices/encountersSlice';
import { fetchPatientDetails } from '../../redux/slices/patientsSlice';
import { fetchPatientPrescriptions } from '../../redux/slices/prescriptionsSlice';
import { 
  Box, 
  Typography,
  CircularProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EncounterBaseRefactored from './EncounterBaseRefactored';
import WorkflowTaskList from './WorkflowTaskList';
import { EncounterType, EncounterStatus } from '../../types/enums';
import { useUserData } from '../../hooks/useUserData';
import { useRefreshUserFacilities } from '../../hooks/useRefreshUserFacilities';
import { useFacilityChangeListener } from '../../hooks/useFacilityChangeListener';
import { fetchFacilityById } from '../../redux/thunks/facilitiesThunks';
import { triggerAIInsights as triggerAIInsightsService } from '../../services/encounterService';
import { updatePatient } from '../../services/patientService';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import PersonIcon from '@mui/icons-material/Person';

// Utility function to sanitize data and convert any remaining Firestore timestamps
const sanitizeData = (data) => {
  // Handle arrays specially to ensure they remain arrays
  if (Array.isArray(data)) {
    return data.map(item => sanitizeData(item));
  }
  
  if (!data || typeof data !== 'object') return data;
  
  const sanitized = { ...data };
  
  Object.keys(sanitized).forEach(key => {
    const value = sanitized[key];
    
    // Check if value is a Firestore Timestamp
    if (value && typeof value === 'object' && value._seconds !== undefined && value._nanoseconds !== undefined) {
      // Convert Timestamp directly to ISO string for Redux serialization
      const date = new Date(value._seconds * 1000 + value._nanoseconds / 1000000);
      sanitized[key] = date.toISOString();
    } 
    // Handle Date objects - convert to ISO string for Redux serialization
    else if (value instanceof Date) {
      sanitized[key] = value.toISOString();
    }
    // Handle nested objects and arrays
    else if (value && typeof value === 'object') {
      if (Array.isArray(value)) {
        sanitized[key] = value.map(item => 
          typeof item === 'object' ? sanitizeData(item) : item
        );
      } else {
        sanitized[key] = sanitizeData(value);
      }
    }
  });
  
  return sanitized;
};

const CreateEncounter = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const selectedPatient = useSelector(state => state.patients.selectedPatient);
  const prescriptions = useSelector(state => state.prescriptions.prescriptions);
  const { user, defaultFacility } = useUserData();
  
  const [selectedEncounterType, setSelectedEncounterType] = useState('');
  const [facility, setFacility] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form data state for the encounter
  const [formData, setFormData] = useState({
    patientId,
    vitals: {
      bloodPressure: '',
      heartRate: '',
      temperature: '',
      respiratoryRate: '',
      oxygenSaturation: '',
      weight: '',
      height: '',
      bmi: ''
    },
    notes: {
      consultationNotes: '',
      secondOpinionNotes: '',
      progressNotes: '',
      nurseNotes: '',
      procedureNotes: '',
      soapNotes: {
        subjective: '',
        objective: '',
        assessment: '',
        plan: ''
      },
      simpleNotes: '',
      phoneNotes: ''
    },
    symptoms: [],
    actionPlan: '',
    prescriptions: [],
    provider: {
      id: user?.uid || '',
      name: user?.displayName || user?.email || '',
      role: user?.role || 'doctor'
    },
    type: '',
    department: '',
    unit: '',
    chiefComplaint: '',
    initialAssessment: '',
    allergies: []
  });

  useRefreshUserFacilities();

  // Handle facility change - redirect to encounters list
  const handleFacilityChange = useCallback((newFacilityId, previousFacilityId) => {
    if (isInitialized) {
      const confirmed = window.confirm(
        'You are changing facilities. This will take you back to the encounters list. Any unsaved changes will be lost. Continue?'
      );
      if (confirmed) {
        navigate('/encounters');
      }
    }
  }, [navigate, isInitialized]);

  useFacilityChangeListener(handleFacilityChange, !isInitialized);

  // Handle inpatient status from navigation state
  useEffect(() => {
    if (location.state?.isInpatient) {
      setSelectedEncounterType(EncounterType.INPATIENT_ADMISSION);
      setFormData(prev => ({ ...prev, type: EncounterType.INPATIENT_ADMISSION }));
    }
  }, [location.state]);

  // Effect for loading patient details
  useEffect(() => {
    if (!patientId) return;
    
    const loadData = async () => {
      try {
        await Promise.all([
          dispatch(fetchPatientDetails(patientId)),
          dispatch(fetchPatientPrescriptions(patientId))
        ]);
      } catch (error) {
        console.error('Error loading patient data:', error);
      }
    };
    loadData();
  }, [patientId, dispatch]);

  // Set initialization flag when patient is loaded
  useEffect(() => {
    if (selectedPatient && !isInitialized) {
      setIsInitialized(true);
    }
  }, [selectedPatient, isInitialized]);

  // Determine facility and encounter types
  useEffect(() => {
    const determineFacilityAndType = async () => {
      if (location.state?.isInpatient) return;
      if (!defaultFacility?.id) return;
      
      try {
        const result = await dispatch(fetchFacilityById(defaultFacility.id));
        if (result?.payload) {
          const foundFacility = result.payload;
          setFacility(foundFacility);
          
          if (Array.isArray(foundFacility.encounterTypes) && foundFacility.encounterTypes.length > 0) {
            // If facility has only one type, auto-select it
            if (foundFacility.encounterTypes.length === 1) {
              const type = foundFacility.encounterTypes[0];
              setSelectedEncounterType(type);
              setFormData(prev => ({ ...prev, type }));
            } else {
              // Default to first type
              setSelectedEncounterType(foundFacility.encounterTypes[0]);
              setFormData(prev => ({ ...prev, type: foundFacility.encounterTypes[0] }));
            }
          }
        }
      } catch (error) {
        console.error('Error fetching facility details:', error);
      }
    };
    
    determineFacilityAndType();
  }, [defaultFacility?.id, location.state?.isInpatient, dispatch]);

  // Update provider info when user changes
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        provider: {
          id: user.uid || '',
          name: user.displayName || user.email || '',
          role: user.role || 'doctor'
        }
      }));
    }
  }, [user]);

  // Handle encounter type selection
  const handleEncounterTypeSelection = (type) => {
    setSelectedEncounterType(type);
    setFormData(prev => ({ ...prev, type }));
  };

  // Handle save - create new encounter
  const handleSave = async () => {
    if (!patientId) {
      alert('Please select a patient before creating an encounter.');
      return;
    }
    
    setSaving(true);
    try {
      const encounterData = {
        ...formData,
        facilityId: defaultFacility?.id,
        status: EncounterStatus.IN_PROGRESS,
        createdAt: new Date().toISOString(),
      };
      
      // Sanitize the encounter data before sending to backend
      const sanitizedEncounterData = sanitizeData(encounterData);
      
      const result = await dispatch(createNewEncounter(sanitizedEncounterData)).unwrap();
      
      // Trigger AI insights asynchronously after encounter creation
      if (result?.id) {
        triggerAIInsightsService(result.id).catch(error => {
          console.error('Error triggering AI insights:', error);
        });
      }
      
      // Update patient's facility if needed
      if (selectedPatient && !selectedPatient.isInpatient && defaultFacility?.id) {
        await updatePatient(selectedPatient.id, { facilityId: defaultFacility.id });
      }
      
      // Navigate to the newly created encounter
      if (result?.id) {
        navigate(`/encounters/${result.id}`);
      } else {
        navigate(`/patients/${patientId}`);
      }
    } catch (error) {
      console.error('Error creating encounter:', error);
      alert('Error creating encounter: ' + (error?.message || error));
    } finally {
      setSaving(false);
    }
  };

  // Handle back navigation
  const handleBack = () => {
    navigate(`/patients/${patientId}`);
  };

  // Render task list for inpatient encounters
  const renderTaskList = () => (
    <WorkflowTaskList encounterId={formData.encounterId} />
  );

  // Get title based on encounter type
  const getTitle = () => {
    if (!selectedPatient) return 'New Patient Encounter';
    
    const patientName = selectedPatient.name || `${selectedPatient.firstName} ${selectedPatient.lastName}`;
    
    switch (selectedEncounterType) {
      case EncounterType.INPATIENT_ADMISSION:
        return `Inpatient Admission for ${patientName}`;
      case EncounterType.OUTPATIENT_VISIT:
        return `Outpatient Visit for ${patientName}`;
      case EncounterType.EMERGENCY_VISIT:
        return `Emergency Visit for ${patientName}`;
      case EncounterType.SURGERY:
        return `Surgical Encounter for ${patientName}`;
      case EncounterType.TELEHEALTH:
        return `Telehealth Visit for ${patientName}`;
      case EncounterType.OBSERVATION:
        return `Observation for ${patientName}`;
      default:
        return `New Encounter for ${patientName}`;
    }
  };

  // Get header icon based on encounter type
  const getHeaderIcon = () => {
    const isInpatient = selectedEncounterType === EncounterType.INPATIENT_ADMISSION;
    return isInpatient 
      ? <LocalHospitalIcon sx={{ fontSize: 32, color: 'primary.main' }} />
      : <PersonIcon sx={{ fontSize: 32, color: 'success.main' }} />;
  };

  // Render encounter type selector
  const renderEncounterTypeSelector = () => {
    if (!facility || !Array.isArray(facility.encounterTypes) || facility.encounterTypes.length <= 1) {
      return null;
    }

    const uniqueEncounterTypes = [...new Set(facility.encounterTypes)];
    
    const getEncounterTypeLabel = (type) => {
      switch (type) {
        case EncounterType.OUTPATIENT_VISIT: return 'Outpatient Visit';
        case EncounterType.EMERGENCY_VISIT: return 'Emergency Visit';
        case EncounterType.INPATIENT_ADMISSION: return 'Inpatient Admission';
        case EncounterType.TELEHEALTH: return 'Telehealth';
        case EncounterType.OBSERVATION: return 'Observation';
        case EncounterType.SURGERY: return 'Surgery';
        case 'homeHealth': return 'Home Health';
        default: return type.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
      }
    };
    
    return (
      <Box sx={{ mb: 2, p: 1.5, background: '#f8f9fa', borderRadius: 1.5, border: '1px solid #e9ecef' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, fontSize: '0.875rem' }}>
          Encounter Type
        </Typography>
        <RadioGroup
          row
          value={selectedEncounterType}
          onChange={e => handleEncounterTypeSelection(e.target.value)}
        >
          {uniqueEncounterTypes.map(type => (
            <FormControlLabel
              key={type}
              value={type}
              control={<Radio size="small" />}
              label={getEncounterTypeLabel(type)}
              sx={{ mr: 1.5, fontSize: '0.8rem' }}
            />
          ))}
        </RadioGroup>
      </Box>
    );
  };

  // Loading state
  if (!patientId) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh', flexDirection: 'column' }}>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
          No Patient Selected
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
          Please select a patient to create an encounter for.
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/encounters')}
          startIcon={<ArrowBackIcon />}
        >
          Back to Encounters
        </Button>
      </Box>
    );
  }

  if (!selectedPatient || !isInitialized) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading Patient Data...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ background: '#f8f9fa', minHeight: '100vh' }}>
      {/* Encounter Type Selector */}
      <Box sx={{ p: 2, pb: 0 }}>
        {renderEncounterTypeSelector()}
      </Box>

      {/* Main Encounter Form using EncounterBaseRefactored */}
      <EncounterBaseRefactored
        mode="create"
        encounter={null}
        patient={selectedPatient}
        prescriptions={prescriptions}
        recentEncounters={[]}
        formData={formData}
        setFormData={setFormData}
        onSave={handleSave}
        onBack={handleBack}
        loading={false}
        saving={saving}
        title={getTitle()}
        subtitle={`MRN: ${selectedPatient.mrn || 'Not assigned'}`}
        renderTaskList={renderTaskList}
        showExtraTabs={true}
        facilityId={defaultFacility?.id || ''}
        encounterType={selectedEncounterType}
        customHeaderIcon={getHeaderIcon()}
        showPatientInfoInHeader={true}
      />
    </Box>
  );
};

export default CreateEncounter;
